import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiService } from "../api/apiService";

export const fetchBotBalance = createAsyncThunk(
    'balance/fetchBotBalance',
    async ({ botId }, { rejectWithValue }) => {
        try {
            // console.log('fetchBotBalance called with:', { botId });
            
            const data = await apiService.getBotBalance(botId);
            // console.log('Balance API response data:', data);

            return {
                botId: botId,
                balanceInfo: data.balanceInfo,
                message: 'Balance fetched successfully'
            };
        }
        catch (error) {
            console.error('Error fetching bot balance:', error);
            return rejectWithValue(error.message);
        }
    }
);

// Fetch balance for multiple bots
export const fetchAllBotBalances = createAsyncThunk(
    'balance/fetchAllBotBalances',
    async ({ botIds }, { dispatch, rejectWithValue }) => {
        try {
            // console.log('fetchAllBotBalances called with:', { botIds });
            
            const promises = botIds.map(botId => 
                dispatch(fetchBotBalance({ botId })).unwrap()
            );
            
            const results = await Promise.allSettled(promises);
            
            const successfulResults = results
                .filter(result => result.status === 'fulfilled')
                .map(result => result.value);
            
            const failedResults = results
                .filter(result => result.status === 'rejected')
                .map(result => result.reason);
            
            if (failedResults.length > 0) {
                console.warn('Some balance fetches failed:', failedResults);
            }
            
            return {
                successful: successfulResults,
                failed: failedResults
            };
        }
        catch (error) {
            console.error('Error fetching all bot balances:', error);
            return rejectWithValue(error.message);
        }
    }
);

const initialState = {
    balances: {}, // Store balances for each bot
    loading: 'idle',
    error: null,
    message: null
};

const balanceSlice = createSlice({
    name: 'balance',
    initialState,
    reducers: {
        resetBalance(state) {
            state.balances = {};
            state.loading = 'idle';
            state.error = null;
            state.message = null;
        },
        clearBalance(state, action) {
            const botId = action.payload;
            delete state.balances[botId];
        },
        clearAllBalances(state) {
            state.balances = {};
        }
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchBotBalance.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
                state.message = null;
            })
            .addCase(fetchBotBalance.fulfilled, (state, action) => {
                state.loading = 'idle';
                const { botId, balanceInfo, message } = action.payload;
                state.balances[botId] = {
                    ...balanceInfo,
                    lastFetched: new Date().toISOString()
                };
                state.message = message;
                state.error = null;
            })
            .addCase(fetchBotBalance.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
                state.message = null;
            })
            .addCase(fetchAllBotBalances.pending, (state) => {
                state.loading = 'pending';
                state.error = null;
                state.message = null;
            })
            .addCase(fetchAllBotBalances.fulfilled, (state, action) => {
                state.loading = 'idle';
                state.message = `Fetched ${action.payload.successful.length} balances successfully`;
                if (action.payload.failed.length > 0) {
                    state.error = `${action.payload.failed.length} balance fetches failed`;
                }
            })
            .addCase(fetchAllBotBalances.rejected, (state, action) => {
                state.loading = 'idle';
                state.error = action.payload || action.error.message;
                state.message = null;
            });
    }
});

export const { resetBalance, clearBalance, clearAllBalances } = balanceSlice.actions;

// Selectors
export const selectBotBalance = (state, botId) => state.balance.balances[botId];
export const selectAllBalances = (state) => state.balance.balances;
export const selectBalanceLoading = (state) => state.balance.loading;
export const selectBalanceError = (state) => state.balance.error;
export const selectBalanceMessage = (state) => state.balance.message;

export default balanceSlice.reducer; 